import { CardClient as Client } from '@atlaskit/link-provider';
import { APIError } from '@atlaskit/linking-common';
import { type JsonLd } from 'json-ld-types';

import { getPreviewPanelBridgeClient } from '@canvas/api-preview/preview-panel-bridge';
import { DurableCache } from '@canvas/durable-cache';
import { hostNotifier, FailureReasons } from '@canvas/host-notifier';

import {
  mockDataCardResponse,
  mockPreviewCardResponse,
} from './smart-link-client-mocks.ts';
import { type SmartLinkData, type Environment } from './smart-link-client-types.ts';
import { extractAri, extractSupportedViews } from './smart-link-client-utils.ts';
import {
  createRevalidationResult,
  getRenderedFieldsSignature,
  mergeRevalidationResult,
  parseRetryAfterMs,
  REVALIDATION_BATCH_SIZE,
  type RevalidationResult,
} from './smart-link-revalidation.ts';

export const hasResolved = (data: JsonLd.Response | undefined) => {
  return !!data;
};

export const hasAccess = (data: JsonLd.Response | undefined) => {
  return data?.meta.access === 'granted';
};

const isAPIError = (err: any): err is APIError => {
  return err && err.name === 'APIError';
};

// There are no `fatal` errors in the object resolver services code but instead these
// exist in the @atlaskit/linking-common library instead. If an error is returned that
// isn't explicitly mapped in their library it will return as fatal
const isFatalError = (error?: Error) => isAPIError(error) && error.kind === 'fatal';

// After talking to the linking platform, we really only need to check if it's a ResolveUnsupportedError. Everything else should be retried by us
// export type APIErrorKind = 'fatal' | 'auth' | 'error' | 'fallback';
// https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/platform/packages/linking-platform/smart-card/src/index.ts
export const isResolveUnsupportedError = (error?: Error) =>
  isAPIError(error) && error.type === 'ResolveUnsupportedError';

type UpdateListener = (
  newData: JsonLd.Response,
  oldData: JsonLd.Response | undefined,
) => void;

type UnsubscribeUpdateListener = () => void;

export const CACHE_TIME_UNAUTHORIZED = 1_000;
export const CACHE_TIME_RESOLVED = 120_000;
export const FAILED_REQUEST_RETRY_MS = 30_000;

export class SmartLinkClient extends Client {
  pendingRequests = new Map();

  private readonly revalidating = new Set<string>();

  private readonly revalidateAgain = new Set<string>();

  private fetchSequence = 0;

  private readonly inFlightFetches = new Map<string, number>();

  private readonly appliedFetches = new Map<string, number>();

  cache = new DurableCache<SmartLinkData>({
    cacheName: 'smart-links',
    loadFromPersistentCache: true,
  });

  private readonly listeners = new Map<string, Set<UpdateListener>>();

  static create(env: Environment = undefined) {
    return new SmartLinkClient(env);
  }

  public async prepare(currentUser: string) {
    await this.cache.prepare(currentUser);
  }

  public prefetchData = () => Promise.resolve(undefined);

  /**
   * Fetches smart link data for a given URL.
   *
   * if the response doesn't exist or if the cache is stale,
   * it refreshes the data and returns the response.
   *
   * if the cache item is valid the response is returned from cache.
   *
   * This function is intended to be called as often as needed
   * as requests are deduplicated and batched internally.
   *
   * @see forceRefreshData if you want to ensure fresh data is retrieved
   */
  public async fetchData(url: string) {
    const entry = this.getCachedData(url);

    if (entry) {
      if (!hasAccess(entry.response) && this.shouldRefresh(url)) {
        return this.forceRefreshData(url);
      }
      return entry.response;
    }

    return this.forceRefreshData(url);
  }

  /**
   * Refreshes smart link data for a given URL, ignoring cache.
   *
   * This function is intended to be called as often as needed
   * as requests are deduplicated internally.
   *
   * @param url The URL to resolve
   * @see fetchData for which accounts for caching
   */
  public async forceRefreshData(url: string) {
    const existingRequest = this.pendingRequests.get(url);
    if (existingRequest) {
      return existingRequest;
    }

    // Grab the cached data before it gets wiped, and provide it to the listeners
    const currData = this.getCachedData(url)?.response;
    this.deleteCachedData(url);

    hostNotifier.reportExternalCheckpoint({
      source: FailureReasons.SMART_LINK_RESOLVE_FAILURE,
      location: 'smart-link-client',
      endpoint: '/gateway/api/object-resolver/resolve/batch',
    });

    const previewPanelBridge = getPreviewPanelBridgeClient();

    const fetchId = this.startFetch(url);
    const newRequest = this.fetchWithIgnoreCache(url)
      .then(async (response) => {
        const data = {
          supportedViews: extractSupportedViews(response),
          response,
          timestamp: Date.now(),
        };

        const nextData = data.response;

        const ari = extractAri(data.response);
        if (ari) {
          const isPreviewPanelAvailable = await previewPanelBridge
            .isPreviewPanelAvailable({ ari })
            .catch(() => undefined);

          if (isPreviewPanelAvailable !== undefined) {
            data.response.meta['whiteboards:previewPanelSupported'] =
              isPreviewPanelAvailable;
          }
        }

        if (!this.claimFetch(url, fetchId)) {
          this.pendingRequests.delete(url);
          return this.getCachedData(url)?.response ?? data.response;
        }

        this.notifyListeners(url, nextData, currData);

        this.setCachedData(url, data);
        this.pendingRequests.delete(url);

        return data.response;
      })
      .catch((e) => {
        setTimeout(() => {
          if (this.pendingRequests.get(url) === newRequest) {
            this.pendingRequests.delete(url);
          }
        }, FAILED_REQUEST_RETRY_MS);

        hostNotifier.reportExternalCheckpoint({
          source: FailureReasons.SMART_LINK_RESOLVE_FAILURE,
          location: 'smart-link-client',
          endpoint: '/gateway/api/object-resolver/resolve/batch',
        });

        // This is a bit hacky but basically if we receive a `fatal` error then the CardSSR
        // component we use will explode. HOWEVER, if we change it to `error` then we can
        // show the error state instead and handle these kinds of problems gracefully.
        // The ONLY scenario where we can receive `fatal` errors is if the @atlaskit/linking-common
        // receives an error type that hasn't been handled. These are retriable for us.
        if (!isResolveUnsupportedError(e) && isFatalError(e)) {
          // Annoyingly, we aren't throwing the original since the `kind` is readonly which
          // means we may lose a bit of the stack trace from inside `fetchData`
          throw new APIError('error', e.hostname, e.message, e.type, e.extensionKey);
        }

        // Throw normally if it's not 'fatal'
        throw e;
      })
      .finally(() => this.finishFetch(url));

    this.pendingRequests.set(url, newRequest);
    return newRequest;
  }

  /**
   * Fetches smart link data for a given URL, ignoring cache. For some reason,
   * the CardClient (super.fetchData) method does not ignore cache even with
   * ignoreCachedValue set to true. This method is used to bypass this issue.
   *
   * @param url The URL to resolve
   * @returns The resolved data
   */
  private async fetchWithIgnoreCache(url: string): Promise<JsonLd.Response> {
    return await fetch('/gateway/api/object-resolver/resolve/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{ resourceUrl: url, ignoreCachedValue: true }]),
    }).then(async (res) => {
      const [data] = await res.json();
      if (data.error) {
        throw new APIError(
          'fallback',
          url,
          data.error.message ?? 'Unknown error',
          data.error.type,
        );
      }
      return data.body;
    });
  }

  private startFetch(url: string): number {
    this.inFlightFetches.set(url, (this.inFlightFetches.get(url) ?? 0) + 1);
    return ++this.fetchSequence;
  }

  private claimFetch(url: string, fetchId: number): boolean {
    if (fetchId < (this.appliedFetches.get(url) ?? 0)) {
      return false;
    }
    this.appliedFetches.set(url, fetchId);
    return true;
  }

  private finishFetch(url: string) {
    const remaining = (this.inFlightFetches.get(url) ?? 1) - 1;
    if (remaining > 0) {
      this.inFlightFetches.set(url, remaining);
      return;
    }
    this.inFlightFetches.delete(url);
    this.appliedFetches.delete(url);
  }

  public async revalidate(urls: readonly string[]): Promise<RevalidationResult> {
    const result = createRevalidationResult();

    const batch: string[] = [];
    for (const url of new Set(urls)) {
      if (this.revalidating.has(url)) {
        this.revalidateAgain.add(url);
        continue;
      }
      batch.push(url);
    }

    for (let start = 0; start < batch.length; start += REVALIDATION_BATCH_SIZE) {
      const chunk = batch.slice(start, start + REVALIDATION_BATCH_SIZE);
      mergeRevalidationResult(result, await this.revalidateChunk(chunk));
    }

    const again = [...this.revalidateAgain].filter((url) => !this.revalidating.has(url));
    if (again.length > 0) {
      for (const url of again) {
        this.revalidateAgain.delete(url);
      }
      mergeRevalidationResult(result, await this.revalidate(again));
    }

    return result;
  }

  private async revalidateChunk(urls: readonly string[]): Promise<RevalidationResult> {
    const result = createRevalidationResult();
    result.requested = urls.length;

    const fetches = new Map(urls.map((url) => [url, this.startFetch(url)]));
    for (const url of urls) {
      this.revalidating.add(url);
    }

    const fail = () => {
      result.failedUrls = [...urls];
      hostNotifier.reportExternalCheckpoint({
        source: FailureReasons.SMART_LINK_RESOLVE_FAILURE,
        location: 'smart-link-client',
        endpoint: '/gateway/api/object-resolver/resolve/batch',
      });
      return result;
    };

    try {
      hostNotifier.reportExternalCheckpoint({
        source: FailureReasons.SMART_LINK_RESOLVE_FAILURE,
        location: 'smart-link-client',
        endpoint: '/gateway/api/object-resolver/resolve/batch',
      });

      const response = await fetch('/gateway/api/object-resolver/resolve/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          urls.map((resourceUrl) => ({ resourceUrl, ignoreCachedValue: true })),
        ),
      });

      if (!response.ok) {
        result.retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'));
        return fail();
      }

      const items: unknown = await response.json();
      if (!Array.isArray(items) || items.length !== urls.length) {
        return fail();
      }

      const resolved: { url: string; response: JsonLd.Response }[] = [];
      for (let i = 0; i < urls.length; i++) {
        const item = items[i] as { body?: JsonLd.Response; error?: unknown } | undefined;
        if (!item?.body || item.error) {
          result.failedUrls.push(urls[i]);
          continue;
        }
        resolved.push({ url: urls[i], response: item.body });
      }

      const previewPanelSupport = await Promise.allSettled(
        resolved.map(({ url, response }) => this.getPreviewPanelSupport(url, response)),
      );

      for (let i = 0; i < resolved.length; i++) {
        const { url, response } = resolved[i];
        if (!this.claimFetch(url, fetches.get(url) ?? 0)) {
          result.unchanged++;
          continue;
        }
        const support = previewPanelSupport[i];
        if (support.status === 'fulfilled' && support.value !== undefined) {
          response.meta['whiteboards:previewPanelSupported'] = support.value;
        }
        if (this.applyRevalidatedResponse(url, response)) {
          result.changed.push(url);
        } else {
          result.unchanged++;
        }
      }

      return result;
    } catch {
      return fail();
    } finally {
      for (const url of urls) {
        this.revalidating.delete(url);
        this.finishFetch(url);
      }
    }
  }

  private getPreviewPanelSupport(
    url: string,
    response: JsonLd.Response,
  ): Promise<boolean | undefined> {
    const ari = extractAri(response);
    if (!ari) {
      return Promise.resolve(undefined);
    }
    const current = this.getCachedData(url)?.response;
    const currentSupport = current?.meta['whiteboards:previewPanelSupported'];
    if (extractAri(current) === ari && typeof currentSupport === 'boolean') {
      return Promise.resolve(currentSupport);
    }
    return getPreviewPanelBridgeClient().isPreviewPanelAvailable({ ari });
  }

  private applyRevalidatedResponse(url: string, response: JsonLd.Response): boolean {
    const current = this.getCachedData(url);
    const changed =
      !current ||
      getRenderedFieldsSignature(current.response) !==
        getRenderedFieldsSignature(response);

    this.setCachedData(url, {
      supportedViews: extractSupportedViews(response),
      response,
      timestamp: Date.now(),
    });
    if (changed) {
      this.notifyListeners(url, response, current?.response);
    }
    return changed;
  }

  /**
   * Returns the cached data immediately.
   * This data is never guaranteed to exist
   * so use with care to avoid accidentally creating race conditions.
   */
  public getCachedData(url: string) {
    return this.cache.get(url);
  }

  public setCachedData(url: string, data: SmartLinkData) {
    this.cache.set(url, data);
  }

  public notifyListeners(
    url: string,
    nextData: JsonLd.Response,
    currData?: JsonLd.Response,
  ) {
    const listeners = this.listeners.get(url);
    listeners?.forEach((listener) => listener(nextData, currData));
  }

  public deleteCachedData(url: string) {
    this.cache.delete(url);
  }

  /**
   * @param url The smart link URL to listen to
   * @param callback Will be called when the client has fetched fresh data
   * @returns A callback that you can use to detach the listener.
   * Note that this must be done in order to avoid memory leaks
   */
  public subscribeToUpdates(
    url: string,
    callback: UpdateListener,
  ): UnsubscribeUpdateListener {
    const callbacks = this.listeners.get(url) ?? new Set();

    if (callbacks.has(callback)) {
      throw new Error(`The same callback was added to ${url} more than once`);
    }

    callbacks.add(callback);

    this.listeners.set(url, callbacks);

    return () => {
      callbacks.delete(callback);
    };
  }

  public shouldRefresh(url: string) {
    const entry = this.cache.get(url);
    if (!entry) {
      return true;
    } else if (!hasAccess(entry.response)) {
      return Date.now() - entry.timestamp > CACHE_TIME_UNAUTHORIZED;
    } else {
      return Date.now() - entry.timestamp > CACHE_TIME_RESOLVED;
    }
  }
}

/* Difficult to mock module without test client in same file  */
class SmartLinkTestClient {
  static create(env: Environment = undefined) {
    const client = SmartLinkClient.create(env);

    const dataCardUrl = 'https://fabric-data-card.com/';
    client.setCachedData(dataCardUrl, {
      response: mockDataCardResponse(dataCardUrl),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    const previewCardUrl = 'https://preview-card.com/';
    client.setCachedData(previewCardUrl, {
      response: mockPreviewCardResponse(previewCardUrl),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    const jiraIssueUrl = 'https://hello.atlassian.net/browse/AUT-14';
    client.setCachedData(jiraIssueUrl, {
      response: mockPreviewCardResponse(jiraIssueUrl, 'jira-object-provider'),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    const jiraIssueUrl2 = 'https://hello.atlassian.net/browse/AUT-15';
    client.setCachedData(jiraIssueUrl2, {
      response: mockPreviewCardResponse(jiraIssueUrl2, 'jira-object-provider'),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    return client;
  }
}

export const smartLinkClient =
  process.env.MODE === 'test' ? SmartLinkTestClient.create() : SmartLinkClient.create();

export type { SmartLinkData } from './smart-link-client-types.ts';
export type { RevalidationResult } from './smart-link-revalidation.ts';
export {
  mockDataCardResponse,
  mockUnauthedDataCardResponse,
} from './smart-link-client-mocks.ts';
export { createUnauthorizedResponse } from './smart-link-client-utils.ts';
